import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { get, post } from "../api/client";
import { useLoad } from "../api/hooks";
import type { Clip, UserClipItem, UserProfile } from "../api/types";
import { Shell } from "../components/Shell";
import { ErrorLine, Sheet, VidFrame, useToast } from "../components/ui";
import { prefetchClipUrls } from "../lib/clipPrefetch";
import { hitLine, numbersOf, placeLine } from "../lib/clips";
import { deliverySummary } from "../lib/descriptions";
import { useIsDesktop } from "../lib/device";
import { exportLabel, useExport } from "../lib/exports";
import { fmtClock } from "../lib/format";

const UP_NEXT_S = 2;
const AUTOPLAY_KEY = "dt.autoplay";

function readAutoplay(): boolean {
  try {
    return localStorage.getItem(AUTOPLAY_KEY) !== "0";
  } catch {
    return true;
  }
}

function writeAutoplay(on: boolean): void {
  try {
    localStorage.setItem(AUTOPLAY_KEY, on ? "1" : "0");
  } catch {
    // ignore
  }
}

function clipRange(src: string): { start: number; end: number } | null {
  const m = /#t=([\d.]+),([\d.]+)/.exec(src);
  return m ? { start: Number(m[1]), end: Number(m[2]) } : null;
}

function asClip(item: UserClipItem): Clip {
  return {
    id: item.id,
    candidate_id: item.id,
    index: item.index,
    window_ms: item.window_ms,
    fitted_ms: null,
    pool: "primary",
    rank: item.index,
    evidence: {},
    events_ms: item.events_ms,
    impact_type: null,
    flagged: false,
    flag_reason: null,
    bowler_id: null,
    batsman_id: null,
    previewed: true,
    metrics: item.metrics,
    clip: {
      playback_url: item.playback_url,
      thumbnail_url: item.thumbnail_url,
      status: "available",
      reason: null,
    },
    artifact_refs: [],
  };
}

export default function UserClipsPage() {
  const { handle: rawHandle = "" } = useParams<{ handle: string }>();
  const handle = rawHandle.replace(/^@+/, "").toLowerCase();
  const [params, setParams] = useSearchParams();
  const videoParam = params.get("video") || "";
  const desktop = useIsDesktop();
  const [toast, say] = useToast();

  // Fetch profile; if ?video= is missing, pick matches[0]?.video.id once loaded.
  const profQ = useLoad<UserProfile>(
    () => get<UserProfile>(`/v1/users/${encodeURIComponent(handle)}${videoParam ? `?video=${encodeURIComponent(videoParam)}` : ""}`),
    [handle, videoParam],
  );

  useEffect(() => {
    if (!videoParam && profQ.data?.clips?.matches?.length) {
      const firstId = profQ.data.clips.matches[0]!.video.id;
      const next = new URLSearchParams(params);
      next.set("video", firstId);
      setParams(next, { replace: true });
    }
  }, [videoParam, profQ.data, params, setParams]);

  const [roleFilter, setRoleFilter] = useState<"" | "batsman" | "bowler">("");
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());
  const [pick, setPick] = useState(0);
  const [auto, setAuto] = useState(readAutoplay);
  const [cancelled, setCancelled] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const [confirmNotMine, setConfirmNotMine] = useState(false);
  const [busyNotMine, setBusyNotMine] = useState(false);

  const [resolvedMap, setResolvedMap] = useState<Record<string, string>>({});
  const resolvedMapRef = useRef<Record<string, string>>({});
  resolvedMapRef.current = resolvedMap;
  const inflightRef = useRef<Set<string>>(new Set());
  const [preparingClip, setPreparingClip] = useState(false);

  const exp = useExport();
  const expAll = useExport();
  useEffect(() => {
    for (const s of [exp.state, expAll.state]) {
      if (s.phase === "ready") say("Your download is ready.");
      if (s.phase === "failed") say(s.message);
    }
  }, [exp.state, expAll.state, say]);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const rawItems = (profQ.data?.clips?.items ?? []).filter((c) => !removedIds.has(c.id));
  const hasBat = rawItems.some((c) => c.roles.includes("batsman"));
  const hasBowl = rawItems.some((c) => c.roles.includes("bowler"));
  const items = roleFilter ? rawItems.filter((c) => c.roles.includes(roleFilter)) : rawItems;

  const idx = Math.min(pick, Math.max(0, items.length - 1));
  const cur = items[idx] ?? null;
  const nextClip = auto && !cancelled && idx + 1 < items.length ? items[idx + 1]! : null;
  const nextRef = useRef(nextClip);
  nextRef.current = nextClip;

  const currentMatch = profQ.data?.clips?.matches.find((m) => m.video.id === videoParam) ?? profQ.data?.clips?.matches[0] ?? null;

  useEffect(() => {
    setCancelled(false);
    setLeft(null);
  }, [cur?.id]);

  const directOrResolved = useCallback((item: UserClipItem | null | undefined): string | null => {
    if (!item) return null;
    if (item.playback_url.includes("#t=")) return item.playback_url;
    return resolvedMap[item.id] ?? null;
  }, [resolvedMap]);

  const resolvedUrl = directOrResolved(cur);

  const backgroundResolve = useCallback((item: UserClipItem | null | undefined) => {
    if (!item || item.playback_url.includes("#t=") || resolvedMapRef.current[item.id] || inflightRef.current.has(item.id)) return;
    inflightRef.current.add(item.id);
    get<{ state: "ready" | "preparing"; url?: string; window_ms?: { start: number; end: number }; whole_match?: boolean }>(
      `/v1/deliveries/${item.id}/playback?redirect=0`,
    )
      .then((res) => {
        if (res.state === "ready" && res.url) {
          const frag = res.whole_match && res.window_ms ? `#t=${res.window_ms.start / 1000},${res.window_ms.end / 1000}` : "";
          const url = `${res.url}${frag}`;
          setResolvedMap((prev) => (prev[item.id] === url ? prev : { ...prev, [item.id]: url }));
        }
      })
      .catch(() => {})
      .finally(() => {
        inflightRef.current.delete(item.id);
      });
  }, []);

  // Resolve clip playback via GET /v1/deliveries/:id/playback?redirect=0 when not already resolved
  useEffect(() => {
    if (!cur) {
      setPreparingClip(false);
      return;
    }
    if (cur.playback_url.includes("#t=") || resolvedMapRef.current[cur.id]) {
      setPreparingClip(false);
      return;
    }
    let cancelledReq = false;
    let timer = 0;

    const checkPlayback = () => {
      get<{ state: "ready" | "preparing"; url?: string; window_ms?: { start: number; end: number }; whole_match?: boolean; retry_after_s?: number }>(
        `/v1/deliveries/${cur.id}/playback?redirect=0`,
      )
        .then((res) => {
          if (cancelledReq) return;
          if (res.state === "ready" && res.url) {
            const frag = res.whole_match && res.window_ms ? `#t=${res.window_ms.start / 1000},${res.window_ms.end / 1000}` : "";
            const url = `${res.url}${frag}`;
            setResolvedMap((prev) => ({ ...prev, [cur.id]: url }));
            setPreparingClip(false);
          } else if (res.state === "preparing") {
            setPreparingClip(true);
            timer = window.setTimeout(checkPlayback, Math.max(1, res.retry_after_s ?? 2) * 1000);
          }
        })
        .catch(() => {
          if (!cancelledReq) {
            setResolvedMap((prev) => ({ ...prev, [cur.id]: cur.playback_url }));
            setPreparingClip(false);
          }
        });
    };

    checkPlayback();
    return () => {
      cancelledReq = true;
      clearTimeout(timer);
    };
  }, [cur?.id, cur?.playback_url]);

  // Pre-resolve and pre-decode upcoming and adjacent clips in the background
  useEffect(() => {
    backgroundResolve(items[idx + 1]);
    backgroundResolve(items[idx + 2]);
    backgroundResolve(items[idx - 1]);
    prefetchClipUrls([
      directOrResolved(items[idx + 1]),
      directOrResolved(items[idx + 2]),
      directOrResolved(items[idx - 1]),
    ]);
  }, [items, idx, backgroundResolve, directOrResolved]);

  const warmUserClip = useCallback((k: number) => {
    if (k === idx) return;
    const target = items[k];
    backgroundResolve(target);
    prefetchClipUrls([
      directOrResolved(target),
      directOrResolved(items[idx + 1]),
      directOrResolved(items[idx + 2]),
      directOrResolved(items[idx - 1]),
    ]);
  }, [items, idx, backgroundResolve, directOrResolved]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !cur) return;
    const onTime = () => {
      const r = clipRange(v.src);
      const rest = r ? r.end - v.currentTime : v.duration - v.currentTime;
      setLeft(nextRef.current && !v.paused && Number.isFinite(rest) && rest > 0 && rest <= UP_NEXT_S ? Math.ceil(rest) : null);
    };
    const onStop = () => {
      const r = clipRange(v.src);
      if (!v.ended && !(r && v.currentTime >= r.end - 0.25 && v.currentTime <= r.end + 2)) return;
      setLeft(null);
      if (nextRef.current) setPick((x) => Math.min(x + 1, items.length - 1));
    };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("pause", onStop);
    v.addEventListener("ended", onStop);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("pause", onStop);
      v.removeEventListener("ended", onStop);
    };
  }, [cur?.id, items.length, resolvedUrl]);

  const toggleAuto = () => {
    const next = !auto;
    setAuto(next);
    writeAutoplay(next);
    setLeft(null);
  };

  const markNotMine = async () => {
    if (!cur || busyNotMine) return;
    setBusyNotMine(true);
    try {
      const role = roleFilter || cur.roles[0] || "batsman";
      await post(`/v1/deliveries/${cur.id}/not-mine`, { role });
      setRemovedIds((prev) => new Set(prev).add(cur.id));
      setConfirmNotMine(false);
      say(`Removed clip #${cur.index} from @${handle}.`);
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusyNotMine(false);
    }
  };

  const n = cur ? numbersOf(asClip(cur)) : null;
  const nextNumbers = nextClip ? numbersOf(asClip(nextClip)) : null;

  const dlThis = (
    <button
      className="btn dark"
      onClick={() =>
        cur &&
        (exp.state.phase === "ready"
          ? window.open(exp.state.url, "_blank")
          : exp.start([cur.id], "mp4", `${handle}_clip${idx + 1}`))
      }
      disabled={!cur || exp.state.phase === "requesting" || exp.state.phase === "preparing"}
      data-testid="user-download-this"
    >
      {exportLabel(exp.state, "Download this")}
    </button>
  );

  const dlAll = (
    <button
      className="btn ghost"
      onClick={() =>
        expAll.state.phase === "ready"
          ? window.open(expAll.state.url, "_blank")
          : expAll.start(items.map((c) => c.id), "mp4", `${handle}_clips`)
      }
      disabled={items.length === 0 || expAll.state.phase === "requesting" || expAll.state.phase === "preparing"}
      data-testid="user-download-all"
    >
      {exportLabel(expAll.state, `All ${items.length}`)}
    </button>
  );

  const upNext = left !== null && nextClip && (
    <div className="upnext" data-testid="up-next" onPointerDown={(e) => e.stopPropagation()}>
      {nextClip.thumbnail_url ? <img src={nextClip.thumbnail_url} alt="" /> : null}
      <div className="un-t">
        <div className="un-s">Up next · in {left} s</div>
        <div className="un-l">
          #{nextClip.index} · {fmtClock(nextClip.window_ms.start)}
          {nextNumbers?.speed != null ? ` · ${nextNumbers.speed} km/h` : ""}
        </div>
      </div>
      <button type="button" onClick={() => { setCancelled(true); setLeft(null); }} data-testid="up-next-cancel">
        Cancel
      </button>
    </div>
  );

  return (
    <Shell
      title={currentMatch ? currentMatch.video.title : `@${handle}`}
      back={`/u/${handle}`}
      active={profQ.data?.is_self ? "Handle" : undefined}
      foot={!desktop && cur ? <div className="grid2">{dlThis}{dlAll}</div> : undefined}
    >
      <div className="p" style={{ paddingTop: desktop ? 24 : 8, paddingBottom: 24 }} data-testid="user-clips-page">
        <ErrorLine error={profQ.error} />
        <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="tiny">
              <Link className="u" to={`/u/${handle}`}>@{handle}</Link>
              {currentMatch?.video.team ? ` · ${currentMatch.video.team.name}` : ""}
            </div>
            {currentMatch && <h2 className="mt4">{currentMatch.video.title}</h2>}
          </div>
          {hasBat && hasBowl && (
            <div className="fpill-bar" data-testid="user-role-filter">
              <button type="button" className={`fpill ${roleFilter === "" ? "on" : ""}`} onClick={() => { setRoleFilter(""); setPick(0); }}>
                All ({rawItems.length})
              </button>
              <button type="button" className={`fpill ${roleFilter === "batsman" ? "on" : ""}`} onClick={() => { setRoleFilter("batsman"); setPick(0); }}>
                Batting
              </button>
              <button type="button" className={`fpill ${roleFilter === "bowler" ? "on" : ""}`} onClick={() => { setRoleFilter("bowler"); setPick(0); }}>
                Bowling
              </button>
            </div>
          )}
        </div>

        {!cur ? (
          <div className="notice mt24" data-testid="user-no-clips">
            {profQ.loading ? "Loading clips…" : `No clips remaining for @${handle} in this match.`}
          </div>
        ) : (
          <div className="mt16" style={desktop ? { display: "grid", gridTemplateColumns: "1fr 300px", gap: 24, alignItems: "start" } : undefined}>
            <div>
              {preparingClip || !resolvedUrl ? (
                <VidFrame
                  height={desktop ? 520 : 300}
                  poster={cur.thumbnail_url}
                  tag={`${idx + 1} of ${items.length} · #${cur.index}`}
                  tr={preparingClip ? "Preparing clip…" : undefined}
                />
              ) : (
                <VidFrame
                  height={desktop ? 520 : 300}
                  src={resolvedUrl}
                  poster={cur.thumbnail_url}
                  videoRef={videoRef}
                  autoPlay
                  fullscreen
                  tag={`${idx + 1} of ${items.length} · #${cur.index} · @${handle}`}
                  description={n ? deliverySummary(`Clip ${idx + 1} of ${items.length} for @${handle}`, n) : undefined}
                >
                  {upNext}
                </VidFrame>
              )}

              {n && (
                <div className="row mt16" style={{ alignItems: "baseline", gap: 8 }}>
                  {n.speed != null ? (
                    <>
                      <span className="big" data-testid="user-clip-speed">{n.speed}</span>
                      <span className="sub">km/h</span>
                      {hitLine(n) && <span className="sub" style={{ marginLeft: 12 }}>{hitLine(n)}</span>}
                    </>
                  ) : (
                    <span className="sub" data-testid="user-clip-speed">—</span>
                  )}
                  <span className="sub" style={{ marginLeft: 12 }}>{placeLine(n)}</span>
                  <div className="row" style={{ marginLeft: "auto", gap: 10 }}>
                    <span className="row" style={{ gap: 8 }}>
                      <span className="tiny" id="user-autoplay-label">Autoplay</span>
                      <button type="button" role="switch" aria-checked={auto} aria-labelledby="user-autoplay-label" className={`sw${auto ? " on" : ""}`} onClick={toggleAuto} data-testid="user-autoplay"><i /></button>
                    </span>
                    {cur.can_report && (
                      <button
                        type="button"
                        className="not-mine-btn"
                        onClick={() => setConfirmNotMine(true)}
                        data-testid="not-mine-btn"
                      >
                        Not my clip
                      </button>
                    )}
                  </div>
                </div>
              )}

              {desktop && (
                <div className="row mt20" style={{ justifyContent: "flex-start", gap: 10 }}>
                  {dlThis}
                  {dlAll}
                </div>
              )}

              {!desktop && (
                <>
                  <div className="row mt20">
                    <span className="cap">{items.length} clips · tap to jump</span>
                    <span className="tiny">#{cur.index} of {items.length}</span>
                  </div>
                  <div className="strip mt6" data-testid="user-clip-strip">
                    {items.map((c, i) => {
                      const cn = numbersOf(asClip(c));
                      return (
                        <button key={c.id} onClick={() => setPick(i)} onPointerEnter={() => warmUserClip(i)} onTouchStart={() => warmUserClip(i)} data-testid={`user-clip-thumb-${c.index}`}>
                          <VidFrame height={56} selected={i === idx} poster={c.thumbnail_url} tag={`#${c.index}`} />
                          <div className="tiny mt6" style={i === idx ? { color: "var(--ink)", fontWeight: 500 } : undefined}>
                            {cn.speed != null ? `${cn.speed} km/h` : fmtClock(c.window_ms.start)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {desktop && (
              <div>
                <div className="row" style={{ paddingBottom: 12 }}>
                  <span className="med">{items.length} clips · @{handle}</span>
                  <span className="tiny">Click to play</span>
                </div>
                <div className="list" style={{ maxHeight: 600, overflowY: "auto" }} data-testid="user-clip-list">
                  {items.map((c, i) => {
                    const cn = numbersOf(asClip(c));
                    return (
                      <div
                        key={c.id}
                        role="button"
                        onClick={() => setPick(i)}
                        onPointerEnter={() => warmUserClip(i)}
                        onTouchStart={() => warmUserClip(i)}
                        style={{
                          cursor: "pointer",
                          background: i === idx ? "var(--bg2)" : undefined,
                          margin: "0 -12px",
                          padding: "10px 12px",
                          borderRadius: 6,
                        }}
                        data-testid={`user-clip-row-${c.index}`}
                      >
                        <VidFrame height={48} selected={i === idx} poster={c.thumbnail_url} style={{ width: 72, flex: "none" }} />
                        <div style={{ flex: 1 }}>
                          <div className="med">
                            #{c.index} · {cn.speed != null ? `${cn.speed} km/h` : fmtClock(c.window_ms.start)}
                            {placeLine(cn) ? ` · ${placeLine(cn)}` : ""}
                          </div>
                          <div className="tiny">
                            {fmtClock(c.window_ms.start)} · {c.roles.map((r) => (r === "batsman" ? "Batting" : "Bowling")).join(", ")}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {confirmNotMine && cur && (
        <Sheet onClose={() => !busyNotMine && setConfirmNotMine(false)}>
          <div data-testid="not-mine-sheet">
            <h2>Remove clip #{cur.index} from @{handle}?</h2>
            <p className="sub mt10">
              This ball disappears from your clips page immediately, and the team manager gets a note so they can reassign it.
            </p>
            <div className="mt20">
              <button
                type="button"
                className="btn dark full"
                disabled={busyNotMine}
                onClick={markNotMine}
                data-testid="confirm-not-mine"
              >
                {busyNotMine ? "Removing…" : "Remove from my clips"}
              </button>
            </div>
            <div className="mt10">
              <button type="button" className="btn ghost full" onClick={() => setConfirmNotMine(false)} disabled={busyNotMine}>
                Cancel
              </button>
            </div>
          </div>
        </Sheet>
      )}
      {toast}
    </Shell>
  );
}
